import { useState, useEffect } from 'react';

// Module-level cache and queue — shared across all hook instances.
// Nominatim allows 1 request/second; we throttle via the queue processor.
const _cache = new Map();  // "lat3,lng3" → address string
const _queue = [];         // { lat3, lng3, resolve }
let _running  = false;

function _round3(n) { return Math.round(n * 1000) / 1000; }
function _key(lat3, lng3) { return `${lat3},${lng3}`; }

async function _processQueue() {
  if (_running) return;
  _running = true;
  while (_queue.length) {
    const { lat3, lng3, resolve } = _queue.shift();
    const cacheKey = _key(lat3, lng3);
    if (_cache.has(cacheKey)) { resolve(_cache.get(cacheKey)); continue; }
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat3}&lon=${lng3}`,
        { headers: { 'Accept-Language': 'en', 'User-Agent': 'NovatrackMDVR/1.0' } }
      );
      const d   = await r.json();
      const a   = d.address || {};
      const road = a.road || a.pedestrian || a.footway || a.path || '';
      const city = a.city || a.town || a.village || a.county || '';
      const addr = [road, city].filter(Boolean).join(', ')
        || (d.display_name || '').split(',').slice(0, 2).join(', ').trim();
      _cache.set(cacheKey, addr);
      resolve(addr);
    } catch {
      resolve(null);
    }
    // Nominatim rate-limit: 1 req/sec
    await new Promise(r => setTimeout(r, 1100));
  }
  _running = false;
}

function _geocode(lat3, lng3) {
  const cacheKey = _key(lat3, lng3);
  if (_cache.has(cacheKey)) return Promise.resolve(_cache.get(cacheKey));
  return new Promise(resolve => {
    _queue.push({ lat3, lng3, resolve });
    _processQueue();
  });
}

export default function useReverseGeocode(pos) {
  const [address, setAddress] = useState(null);

  // Round to 3 dp (~100m) so GPS drift doesn't re-trigger geocoding
  const lat3 = pos?.lat != null ? _round3(pos.lat) : null;
  const lng3 = pos?.lng != null ? _round3(pos.lng) : null;

  useEffect(() => {
    if (lat3 == null || lng3 == null) { setAddress(null); return; }
    let cancelled = false;
    _geocode(lat3, lng3).then(addr => { if (!cancelled) setAddress(addr); });
    return () => { cancelled = true; };
  }, [lat3, lng3]);

  return address;  // string | null
}
